const BASE = "/api";
const ACTIVE_COMPANY_KEY = "chro.activeCompanyId";

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
  }
}

export type ApiResponseSchema<T> = {
  parse(value: unknown): T;
};

export async function api<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const activeCompanyId = selectedCompanyId();
  const headers: Record<string, string> = {
    accept: "application/json",
    ...(activeCompanyId ? { "x-company-id": activeCompanyId } : {}),
    ...((init.headers as Record<string, string> | undefined) ?? {}),
  };
  // Only declare a JSON content-type when there's actually a body — Fastify
  // rejects an empty body sent with content-type application/json
  // (FST_ERR_CTP_EMPTY_JSON_BODY), which breaks no-body POSTs like confirm.
  if (init.body != null && headers["content-type"] === undefined && headers["Content-Type"] === undefined) {
    headers["content-type"] = "application/json";
  }
  const res = await fetch(BASE + path, {
    credentials: "include",
    ...init,
    headers,
  });
  const text = await res.text();
  const body = text ? JSON.parse(text) : null;
  if (!res.ok) {
    throw new ApiError(res.status, (body && (body.error ?? res.statusText)) || res.statusText, body);
  }
  return body as T;
}

/** Use at guarded HTTP boundaries so runtime JSON and TypeScript cannot drift independently. */
export async function apiContract<T>(
  schema: ApiResponseSchema<T>,
  path: string,
  init: RequestInit = {}
): Promise<T> {
  return schema.parse(await api<unknown>(path, init));
}

export function selectedCompanyId(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(ACTIVE_COMPANY_KEY);
}

export function setSelectedCompanyId(companyId: string | null): void {
  if (typeof window === "undefined") return;
  if (companyId) window.localStorage.setItem(ACTIVE_COMPANY_KEY, companyId);
  else window.localStorage.removeItem(ACTIVE_COMPANY_KEY);
}
